import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  coerceMultiValue,
  getOptionDisabled,
  getOptionLabel,
  getOptionValue,
} from './optionUtils';

/**
 * Reusable Select wrapper for single + multi.
 *
 * Options can be:
 * - string[]
 * - { value, label, disabled? }[]
 *
 * Multi-select values render as chips with quick remove.
 */
export default function SelectField({
  label,
  options = [],
  value,
  onChange,
  multiple = false,
  size = 'small',
  fullWidth = false,
  sx,
  variant,
  emptyLabel = 'All',
  includeEmptyOption = false,
  emptyValue = '',
  renderValue, // optional override
  children,
  required = false,
  ...selectProps
}) {
  const normalizedValue = multiple ? coerceMultiValue(value) : (value ?? '');

  const valueToLabel = useMemo(() => {
    const m = new Map();
    for (const opt of options) {
      m.set(getOptionValue(opt), getOptionLabel(opt));
    }
    return m;
  }, [options]);

  const handleRemove = (val) => {
    if (!multiple) return;
    const current = coerceMultiValue(normalizedValue);
    const next = current.filter((x) => x !== val);
    onChange?.(next);
  };

  const multiSelected = multiple ? coerceMultiValue(normalizedValue) : [];

  // displayEmpty + renderValue shows text (e.g. "All") even when value is ''.
  // MUI would otherwise leave InputLabel un-shrunk and it overlaps that text.
  const labelShrink =
    Boolean(label) &&
    (typeof renderValue === 'function' ||
      multiSelected.length > 0 ||
      (!multiple && normalizedValue !== '' && normalizedValue != null) ||
      Boolean(emptyLabel));

  const defaultRenderValue = (selected) => {
    if (!multiple) {
      if (selected == null || selected === '') return emptyLabel;
      return valueToLabel.get(selected) ?? String(selected);
    }

    const values = coerceMultiValue(selected);
    if (values.length === 0) return emptyLabel;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {values.map((v) => (
          <Chip
            key={String(v)}
            size="small"
            label={valueToLabel.get(v) ?? String(v)}
            deleteIcon={<CloseIcon />}
            onMouseDown={(e) => e.stopPropagation()}
            onDelete={() => handleRemove(v)}
          />
        ))}
      </Box>
    );
  };

  return (
    <FormControl variant={variant} size={size} fullWidth={fullWidth} sx={sx} required={required}>
      {label ? (
        <InputLabel shrink={labelShrink} required={required}>{label}</InputLabel>
      ) : null}
      <Select
        multiple={multiple}
        value={normalizedValue}
        label={label}
        onChange={(e) => {
          const next = e.target.value;
          if (multiple) {
            // MUI can return string in some edge cases (autofill). Normalize.
            if (typeof next === 'string') {
              onChange?.(next.split(',').filter(Boolean));
            } else {
              onChange?.(Array.isArray(next) ? next : coerceMultiValue(next));
            }
          } else {
            onChange?.(next);
          }
        }}
        renderValue={renderValue || defaultRenderValue}
        displayEmpty
        {...selectProps}
      >
        {includeEmptyOption && (
          <MenuItem value={emptyValue}>{emptyLabel}</MenuItem>
        )}
        {children ||
          options.map((opt) => {
            const val = getOptionValue(opt);
            const lab = getOptionLabel(opt);
            const disabled = getOptionDisabled(opt);
            return (
              <MenuItem key={String(val)} value={val} disabled={disabled}>
                {lab}
              </MenuItem>
            );
          })}
      </Select>
    </FormControl>
  );
}

